import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { DateTimePicker } from 'components/shared';
import { images } from 'assets';
import '../components/finances/print-styles.css';
import financeStore from 'store/financeStore';
import { theme } from 'config/theme';
import globalStore from 'store/globalStore';
import { DateHistoryData } from './finances';

import styles from '../styles';
import { useAppSelector } from 'index';
import agent from 'api/agent';
import { Roles } from 'constants/enums';
import { createDateIgnoreTimezone, formatDateTimeGermany } from 'helpers/datetimeHelper';
import { useParams } from 'react-router';
import dayjs from 'dayjs';

export enum DateFilterType {
    CLOSING = 0,
    DAY = 1,
    WEEK = 2,
    MONTH = 3,
    YEAR = 4,
    CUSTOM = 5,
}

interface DateFiltersProps {
    dateFilter: number;
    setDateFilter: (filter: number) => void;
    getData: (
        dayAgo: number,
        dateHistoryList?: DateHistoryData[],
        dateFrom?: Date,
        dateTo?: Date
    ) => void;
    restaurantId?: string;
    selectedDatesHistory: DateHistoryData[];
    setSelectedDatesHistory: (dateHistory: DateHistoryData[]) => void;
    isChartsShowing?: boolean;
    onDateRangeChange?: (dateFrom: Date | null, dateTo: Date | null) => void;
    showClosing?: boolean;
}

export default function DateFilters(props: DateFiltersProps) {
    const classes = styles();
    const dispatch = useDispatch();
    const { showClosing = true } = props;
    const user = useAppSelector((state) => state.user);
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [showDateFromPicker, setShowDateFromPicker] = useState(false);
    const [showDateToPicker, setShowDateToPicker] = useState(false);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);

    const params = useParams<{ id: string }>();
    const dateFilters = [];

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentMonthDay = currentDate.getDate();

    function prepareDataRange(dateFromUnix: number) {
        const dateHistoryList = [];
        const dateFrom = new Date(dateFromUnix);
        const offsetDiff = dayjs().tz(restaurant.timeZone).utcOffset() - dayjs().utcOffset();
        const dateTo = dayjs().add(offsetDiff, 'minutes').toDate();

        dateHistoryList.push({
            name: `${formatDateTimeGermany(dateFrom)} - ${formatDateTimeGermany(dateTo)}`,
            startDate: dateFrom,
            endDate: dateTo,
            isCustom: true,
        });

        return { dateFrom, dateTo, dateHistoryList };
    }

    async function getDataFromLastClosing() {
        dispatch(globalStore.actionCreators.showSpiner());
        let dateFromUnix = 0;
        const restId = user.role === Roles.ADMIN ? params.id : '';
        try {
            const closing = await agent.Payments.GetLastClosingReport(restId);
            dateFromUnix = closing?.dateTo ?? dayjs().utcOffset(0).startOf('year').valueOf();
            const { dateFrom, dateTo, dateHistoryList } = prepareDataRange(dateFromUnix);
            setDateFrom(createDateIgnoreTimezone(dateFromUnix));
            setDateTo(dateTo);
            props.getData(0, dateHistoryList, dateFrom, dateTo);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    function closeDatePickers() {
        setShowDateRangePicker(false);
        setShowDateFromPicker(false);
        setShowDateToPicker(false);
    }

    function onSelectedDateHistoryClick(clickedHistory: {
        name: string;
        startDate: Date;
        endDate: Date;
        index: number;
    }) {
        const lastHistoryIndex = props.selectedDatesHistory.length - 1;
        const { index, startDate, endDate } = clickedHistory;
        if (index !== lastHistoryIndex) {
            const filteredDatesHistory = props.selectedDatesHistory.filter(
                (item, i) => index + 1 > i
            );
            const newDateFilter = 4 - index;
            dispatch(
                financeStore.actionCreators.getFinance(
                    startDate,
                    endDate,
                    user.role !== Roles.ADMIN ? '' : props.restaurantId
                )
            );
            props.setSelectedDatesHistory(filteredDatesHistory);
            //props.setDateFilter(newDateFilter);
        }
    }

    function handleFilterClick(filter: DateFilterType, dateHistoryList: DateHistoryData[] = []) {
        props.setDateFilter(filter);
        switch (filter) {
            case DateFilterType.CLOSING:
                getDataFromLastClosing();
                break;
            case DateFilterType.DAY:
                const todayStart = new Date(Date.now());
                const todayEnd = new Date(Date.now());
                todayStart.setHours(0, 0, 0, 0);
                setDateFrom(todayStart);
                todayEnd.setHours(23, 59, 59, 999);
                setDateTo(todayEnd);
                props.getData(0, dateHistoryList, todayStart, todayEnd);
                break;
            case DateFilterType.WEEK:
                const today = new Date();
                const day = today.getDay();
                const diff = today.getDate() - day + (day === 0 ? -6 : 1);
                const weekStart = new Date(today.setDate(diff));
                weekStart.setHours(0, 0, 0, 0);
                setDateFrom(weekStart);
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                weekEnd.setHours(23, 59, 59, 999);
                setDateTo(weekEnd);
                props.getData(0, dateHistoryList, weekStart, weekEnd);
                break;
            case DateFilterType.MONTH:
                const monthStart = new Date(currentYear, currentMonth, 1);
                monthStart.setHours(0, 0, 0, 0);
                setDateFrom(monthStart);
                const monthEnd = new Date(currentYear, currentMonth + 1, 0);
                monthEnd.setHours(23, 59, 59, 999);
                setDateTo(monthEnd);
                props.getData(0, dateHistoryList, monthStart, monthEnd);
                break;
            case DateFilterType.YEAR:
                const yearStart = new Date(currentYear, 0, 1);
                yearStart.setHours(0, 0, 0, 0);
                setDateFrom(yearStart);
                const yearEnd = new Date(currentYear, 11, 31);
                yearEnd.setHours(23, 59, 59, 999);
                setDateTo(yearEnd);
                props.getData(0, dateHistoryList, yearStart, yearEnd);
                break;
        }
    }

    function handleFilterShift(backward: boolean) {
        let newDateFrom: Date;
        let newDateTo: Date;
        let currYear: number = 0;
        let currMonth: number = 0;
        let currMonthDay: number = 0;
        let dateHistoryList: DateHistoryData[] = props.selectedDatesHistory.slice();

        switch (props.dateFilter) {
            case DateFilterType.DAY:
                // Shift by 1 day
                newDateFrom = new Date(dateFrom || new Date());
                newDateTo = new Date(dateTo || new Date());

                const dayShift = backward ? -1 : 1;
                newDateFrom.setDate(newDateFrom.getDate() + dayShift);
                newDateTo.setDate(newDateTo.getDate() + dayShift);

                // Ensure time boundaries
                newDateFrom.setHours(0, 0, 0, 0);
                newDateTo.setHours(23, 59, 59, 999);

                currYear = newDateFrom.getFullYear();
                currMonth = newDateFrom.getMonth();
                currMonthDay = newDateFrom.getDate();

                setDateFrom(newDateFrom);
                setDateTo(newDateTo);
                dateHistoryList[dateHistoryList.length - 1] = {
                    name: `${currMonthDay} ${i18n.t('common.monthNames.' + currMonth)} ${currYear}`,
                    startDate: newDateFrom,
                    endDate: newDateTo,
                };

                props.getData(0, dateHistoryList, newDateFrom, newDateTo);
                break;

            case DateFilterType.WEEK:
                // Shift by 1 week (7 days)
                newDateFrom = new Date(dateFrom || new Date());
                newDateTo = new Date(dateTo || new Date());

                const weekShift = backward ? -7 : 7;
                newDateFrom.setDate(newDateFrom.getDate() + weekShift);
                newDateTo.setDate(newDateTo.getDate() + weekShift);

                // Ensure time boundaries
                newDateFrom.setHours(0, 0, 0, 0);
                newDateTo.setHours(23, 59, 59, 999);

                setDateFrom(newDateFrom);
                setDateTo(newDateTo);

                let weekCount = Math.ceil(newDateFrom.getDate() / 7);
                currYear = newDateFrom.getFullYear();
                currMonth = newDateFrom.getMonth();
                currMonthDay = newDateFrom.getDate();
                dateHistoryList[0] = {
                    name: `${i18n.t('common.monthNames.' + currMonth)} ${currYear}`,
                    startDate: new Date(currYear, currMonth, currMonthDay),
                    endDate: new Date(currYear, currMonth, currMonthDay + 7),
                };
                dateHistoryList[dateHistoryList.length - 1] = {
                    name: `${weekCount} ${i18n.t('common.week')}`,
                    startDate: newDateFrom,
                    endDate: newDateTo,
                };
                props.getData(0, dateHistoryList, newDateFrom, newDateTo);
                break;

            case DateFilterType.MONTH:
                // Shift by 1 month
                const currentFromDate = dateFrom || new Date();
                const monthShift = backward ? -1 : 1;

                newDateFrom = new Date(
                    currentFromDate.getFullYear(),
                    currentFromDate.getMonth() + monthShift,
                    1
                );
                newDateFrom.setHours(0, 0, 0, 0);

                // Last day of the new month
                newDateTo = new Date(newDateFrom.getFullYear(), newDateFrom.getMonth() + 1, 0);
                newDateTo.setHours(23, 59, 59, 999);

                setDateFrom(newDateFrom);
                setDateTo(newDateTo);
                currYear = newDateFrom.getFullYear();
                currMonth = newDateFrom.getMonth();
                currMonthDay = newDateFrom.getDate();

                dateHistoryList[dateHistoryList.length - 1] = {
                    name: `${i18n.t('common.monthNames.' + currMonth)} ${currYear}`,
                    startDate: new Date(currYear, currMonth, 1),
                    endDate: new Date(currYear, currMonth + 1, 1),
                };
                props.getData(0, dateHistoryList, newDateFrom, newDateTo);
                break;

            case DateFilterType.YEAR:
                // Shift by 1 year
                const currentFromYear = dateFrom?.getFullYear() || new Date().getFullYear();
                const yearShift = backward ? -1 : 1;
                const newYear = currentFromYear + yearShift;

                newDateFrom = new Date(newYear, 0, 1); // January 1st
                newDateFrom.setHours(0, 0, 0, 0);

                newDateTo = new Date(newYear, 11, 31); // December 31st
                newDateTo.setHours(23, 59, 59, 999);

                currYear = newDateFrom.getFullYear();
                currMonth = newDateFrom.getMonth();
                currMonthDay = newDateFrom.getDate();
                dateHistoryList[dateHistoryList.length - 1] = {
                    name: `${currentYear}`,
                    startDate: new Date(currYear, 0, 1),
                    endDate: new Date(currYear + 1, 0, 1),
                };
                setDateFrom(newDateFrom);
                setDateTo(newDateTo);
                props.getData(0, dateHistoryList, newDateFrom, newDateTo);
                break;

            case DateFilterType.CUSTOM:
                if (!dateFrom || !dateTo) return;

                // Calculate the difference in days between from and to
                const timeDiff = dateTo.getTime() - dateFrom.getTime();
                const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

                if (backward) {
                    // New DateTo becomes previous DateFrom with same time
                    newDateTo = new Date(dateFrom);
                    newDateTo.setHours(
                        dateTo.getHours(),
                        dateTo.getMinutes(),
                        dateTo.getSeconds(),
                        dateTo.getMilliseconds()
                    );

                    // New DateFrom is shifted back by the days difference, keeping the time from original DateFrom
                    newDateFrom = new Date(dateFrom);
                    newDateFrom.setDate(newDateFrom.getDate() - daysDiff);
                } else {
                    // New DateFrom becomes previous DateTo with time from original DateFrom
                    newDateFrom = new Date(dateTo);
                    newDateFrom.setHours(
                        dateFrom.getHours(),
                        dateFrom.getMinutes(),
                        dateFrom.getSeconds(),
                        dateFrom.getMilliseconds()
                    );

                    // New DateTo is shifted forward by the days difference, keeping the time from original DateTo
                    newDateTo = new Date(dateTo);
                    newDateTo.setDate(newDateTo.getDate() + daysDiff);
                }

                setDateFrom(newDateFrom);
                setDateTo(newDateTo);

                // Update the date history with new custom range
                dateHistoryList[dateHistoryList.length - 1] = {
                    name: `${formatDateTimeGermany(newDateFrom)} - ${formatDateTimeGermany(
                        newDateTo
                    )}`,
                    startDate: newDateFrom,
                    endDate: newDateTo,
                    isCustom: true,
                };

                props.getData(0, dateHistoryList, newDateFrom, newDateTo);
                break;

            default:
                break;
        }
    }

    function getElements() {
        const elements: any = [];

        props.selectedDatesHistory.forEach((eachItem, i) => {
            const { name } = eachItem;

            if (i === 0) {
                elements.push(
                    <div
                        key={i}
                        className={classes.cursorHover}
                        onClick={() =>
                            onSelectedDateHistoryClick({
                                ...eachItem,
                                index: i,
                            })
                        }
                    >
                        {name}
                    </div>
                );
            } else {
                elements.push(
                    <div
                        key={i}
                        className={`${classes.flexRow} ${classes.cursorHover}`}
                        onClick={() =>
                            onSelectedDateHistoryClick({
                                ...eachItem,
                                index: i,
                            })
                        }
                    >
                        <img
                            src={images.icons.rightArrow}
                            alt="right-arrow"
                            className={classes.mHorizontal15}
                        />
                        <span className={classes.secondDateHistoryText}>{name}</span>
                    </div>
                );
            }
        });

        return elements;
    }

    if (showClosing) {
        dateFilters.push(
            <Tooltip
                key={Math.random().toString(16).substring(2)}
                title={i18n.t('messages.closingFilterTooltip')}
                style={{ marginRight: 10 }}
                arrow
            >
                <InfoOutlinedIcon color="primary" fontSize="small" />
            </Tooltip>
        );
    }

    for (let i = 0; i <= 4; i++) {
        if (!showClosing && i === DateFilterType.CLOSING) {
            continue;
        }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentMonthDay = currentDate.getDate();

        let title,
            dateHistoryList: DateHistoryData[] = [];
        switch (i) {
            case DateFilterType.CLOSING:
                title = i18n.t('common.closing');
                break;
            case DateFilterType.DAY:
                title = i18n.t('common.day');
                dateHistoryList.push({
                    name: `${currentMonthDay} ${i18n.t(
                        'common.monthNames.' + currentMonth
                    )} ${currentYear}`,
                    startDate: new Date(currentYear, currentMonth, currentMonthDay),
                    endDate: new Date(currentYear, currentMonth, currentMonthDay),
                });
                break;
            case DateFilterType.WEEK:
                title = i18n.t('common.week');
                dateHistoryList.push({
                    name: `${i18n.t('common.monthNames.' + currentMonth)} ${currentYear}`,
                    startDate: new Date(currentYear, currentMonth, currentMonthDay),
                    endDate: new Date(currentYear, currentMonth, currentMonthDay + 7),
                });
                const d = new Date();
                const day = d.getDay(),
                    diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
                let startingDay = new Date(d.setDate(diff)).getDate();
                let weekCount = Math.ceil(currentMonthDay / 7);
                if (weekCount > 1 || weekCount >= 5) {
                    // @ts-ignore
                    // startingDay += getExtraDaysUntil(d.getDay(), 1);
                }
                let endDay = startingDay + 7;
                endDay = endDay > 31 ? 31 : endDay;
                dateHistoryList.push({
                    name: `${weekCount} ${i18n.t('common.week')}`,
                    startDate: new Date(currentYear, currentMonth, startingDay),
                    endDate: new Date(currentYear, currentMonth, endDay),
                });
                break;
            case DateFilterType.MONTH:
                title = i18n.t('common.month');
                dateHistoryList.push({
                    name: `${i18n.t('common.monthNames.' + currentMonth)} ${currentYear}`,
                    startDate: new Date(currentYear, currentMonth, 1),
                    endDate: new Date(currentYear, currentMonth + 1, 1),
                });
                break;
            case DateFilterType.YEAR:
                title = i18n.t('common.year');
                dateHistoryList.push({
                    name: `${currentYear}`,
                    startDate: new Date(currentYear, 0, 1),
                    endDate: new Date(currentYear + 1, 0, 1),
                });
                break;
        }
        dateFilters.push(
            <Button
                key={Math.random().toString(16).substring(2)}
                style={i !== props.dateFilter ? { color: theme.palette.primary.main } : {}}
                onClick={() => handleFilterClick(i, dateHistoryList)}
                className={classes.mRight20}
                type="button"
                color="primary"
                // @ts-ignore
                variant={i === props.dateFilter ? 'contained' : undefined}
            >
                {title}
            </Button>
        );
    }

    dateFilters.push(
        <IconButton
            key={Math.random().toString(16).substring(2)}
            aria-label="delete"
            size="small"
            style={{
                borderRadius: 4,
                backgroundColor:
                    props.dateFilter === DateFilterType.CUSTOM
                        ? theme.palette.primary.main
                        : 'transparent',
            }}
            onClick={() => setShowDateRangePicker(!showDateRangePicker)}
        >
            <CalendarTodayIcon
                fontSize="small"
                style={{
                    color:
                        props.dateFilter === DateFilterType.CUSTOM
                            ? theme.palette.common.white
                            : theme.palette.primary.main,
                }}
            />
        </IconButton>
    );

    useEffect(() => {
        if (showClosing) {
            getDataFromLastClosing();
        } else {
            handleFilterClick(props.dateFilter);
        }
    }, []);

    function validateAndSubmitDateChange(newDate: Date, isDateFrom: boolean) {
        const currentDateFrom = isDateFrom ? newDate : dateFrom;
        const currentDateTo = isDateFrom ? dateTo : newDate;

        if (currentDateFrom && currentDateTo) {
            const timeFrom = currentDateFrom.getTime();
            const timeTo = currentDateTo.getTime();
            if (timeTo < timeFrom) {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'warning',
                        i18n.t('warnings.dateToCannotBeOlderThanDateFrom')
                    )
                );
                return;
            } else if (timeFrom === timeTo) {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'warning',
                        i18n.t('warnings.dateFromAndDateToCannotBeTheSame')
                    )
                );
                return;
            }
        }

        if (isDateFrom) {
            setDateFrom(newDate);
        } else {
            setDateTo(newDate);
        }

        const dateHistoryList = [...props.selectedDatesHistory];
        const firstDate = { ...dateHistoryList[0] };
        if (isDateFrom) {
            firstDate.startDate = newDate;
        } else {
            firstDate.endDate = newDate;
        }

        dateHistoryList[0] = {
            name: `${formatDateTimeGermany(firstDate.startDate)} - ${formatDateTimeGermany(
                firstDate.endDate
            )}`,
            startDate: firstDate.startDate,
            endDate: firstDate.endDate,
            isCustom: true,
        };

        props.setDateFilter(DateFilterType.CUSTOM);
        props.getData(
            0,
            dateHistoryList,
            currentDateFrom || new Date(),
            currentDateTo || new Date()
        );

        setTimeout(() => {
            closeDatePickers();
        }, 0);
    }

    useEffect(() => {
        props.onDateRangeChange?.(dateFrom, dateTo);
    }, [dateFrom, dateTo]);

    return (
        <div className={classes.flexRowSpaceBetween} style={{ flexGrow: 1, position: 'relative' }}>
            <div className={classes.flexRow}>
                <IconButton
                    aria-label="delete"
                    size="small"
                    style={{
                        color: theme.palette.common.black,
                        opacity: props.dateFilter === DateFilterType.CLOSING ? 0.5 : 1,
                    }}
                    onClick={() => handleFilterShift(true)}
                    disabled={props.dateFilter === DateFilterType.CLOSING}
                >
                    <ArrowBackIcon fontSize="inherit" />
                </IconButton>
                <div className={classes.cursorHover} onClick={() => setShowDateFromPicker(true)}>
                    {formatDateTimeGermany(dateFrom)}
                    {showDateFromPicker && (
                        <div
                            className={classes.timePickerContainer}
                            style={{ left: 200, right: 'auto' }}
                        >
                            <DateTimePicker
                                showButtons={true}
                                isDateRange={false}
                                date={dateFrom || new Date()}
                                onSubmit={(date) => {
                                    validateAndSubmitDateChange(date, true);
                                }}
                                isOpen={showDateFromPicker}
                                onClose={() => {
                                    setTimeout(() => {
                                        closeDatePickers();
                                    }, 0);
                                }}
                            />
                        </div>
                    )}
                </div>
                <span style={{ margin: '0 10px' }}>-</span>
                <div className={classes.cursorHover} onClick={() => setShowDateToPicker(true)}>
                    {formatDateTimeGermany(dateTo)}
                    {showDateToPicker && (
                        <div
                            className={classes.timePickerContainer}
                            style={{ left: 350, right: 'auto' }}
                        >
                            <DateTimePicker
                                showButtons={true}
                                isDateRange={false}
                                date={dateTo || new Date()}
                                onSubmit={(date) => {
                                    validateAndSubmitDateChange(date, false);
                                }}
                                isOpen={showDateToPicker}
                                onClose={() => {
                                    setTimeout(() => {
                                        closeDatePickers();
                                    }, 0);
                                }}
                            />
                        </div>
                    )}
                </div>
                <IconButton
                    aria-label="delete"
                    size="small"
                    style={{
                        color: theme.palette.common.black,
                        opacity: props.dateFilter === DateFilterType.CLOSING ? 0.5 : 1,
                    }}
                    onClick={() => handleFilterShift(false)}
                    disabled={props.dateFilter === DateFilterType.CLOSING}
                >
                    <ArrowForwardIcon fontSize="inherit" />
                </IconButton>
            </div>
            {/* {props.isChartsShowing && (
                <div
                    className={classes.flexRow}
                    style={{ position: 'absolute', top: 30, left: 25 }}
                >
                    {getElements()}
                </div>
            )} */}
            {showDateRangePicker && (
                <div className={classes.timePickerContainer}>
                    <DateTimePicker
                        showButtons={true}
                        isDateRange={true}
                        date={new Date()}
                        futureTimeOnly={true}
                        onSubmit={(pickedDates) => {
                            if (pickedDates && !pickedDates.includes(null)) {
                                const dateHistoryList = [];
                                console.log(pickedDates);
                                const dateFrom = pickedDates[0];
                                const dateTo = pickedDates[1];
                                dateHistoryList.push({
                                    name: `${formatDateTimeGermany(
                                        dateFrom
                                    )} - ${formatDateTimeGermany(dateTo)}`,
                                    startDate: dateFrom,
                                    endDate: dateTo,
                                    isCustom: true,
                                });

                                props.setDateFilter(5);
                                setDateFrom(dateFrom);
                                setDateTo(dateTo);
                                return props.getData(0, dateHistoryList, dateFrom, dateTo);
                            } else {
                                dispatch(
                                    globalStore.actionCreators.showToaster(
                                        'warning',
                                        i18n.t('warnings.selectTwoDatesToFilter')
                                    )
                                );
                            }
                        }}
                        isOpen={showDateRangePicker}
                        onClose={closeDatePickers}
                    />
                </div>
            )}
            <div className={classes.flexRow}>{dateFilters}</div>
        </div>
    );
}
